import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

// Drop-in replacement for `convert-react-to-web-component` that uses the
// React 18 `createRoot` API instead of the deprecated `ReactDOM.render`.
// Uses a per-instance Emotion cache that inserts MUI styles into the shadow
// DOM instead of document.head, preventing CSS leakage into the host app..

const toCamelCase = (value) =>
    value
        .split('-')
        .map((part, index) => (index !== 0 ? `${part.charAt(0).toUpperCase()}${part.substring(1)}` : part))
        .join('');

const toKebabCase = (value) =>
    value
        .split(/(?=[A-Z])/)
        .join('-')
        .toLowerCase();

const createWebComponentClass = (Component, options) =>
    class extends HTMLElement {
        constructor() {
            super();
            this.props = {};
            this._root = null;
            if (options.shadowDom) {
                this.attachShadow({ mode: options.shadowDom });
            }
        }

        static get observedAttributes() {
            return options?.attributes ? options.attributes.map(toKebabCase) : [];
        }

        connectedCallback() {
            this.render();
        }

        disconnectedCallback() {
            if (this._root) {
                this._root.unmount();
                this._root = null;
            }
        }

        attributeChangedCallback(name, oldValue, newValue) {
            this.props[toCamelCase(name)] = options?.middleware(newValue) || newValue;
            this.render();
        }

        render() {
            if (!this.isConnected) {
                return;
            }
            const container = options.shadowDom ? this.shadowRoot : this;
            if (!this._root) {
                this._root = createRoot(container);
            }
            // For shadow DOM, create an emotion cache that inserts MUI <style> tags
            // into the shadow root instead of document.head.  This prevents styles
            // from twinworld-component leaking into the host app (twinup-community-app).
            // The cache is created once (guarded) so the CacheProvider value is stable
            // across re-renders — React sees no change and doesn't remount children.
            let element = createElement(Component, Object.assign({}, this.props), null);
            if (options.shadowDom && this.shadowRoot) {
                if (!this._emotionCache) {
                    this._emotionCache = createCache({
                        key: 'tw-mui',
                        container: this.shadowRoot,
                        prepend: true
                    });
                }
                element = createElement(CacheProvider, { value: this._emotionCache }, element);
            }
            this._root.render(element);
        }
    };

const reactToWebComponent = (Component, options) => {
    const ComponentClass = createWebComponentClass(Component, options);
    const name = options?.name || toKebabCase(Component.name);
    if (customElements.get(name) === undefined) {
        customElements.define(name, ComponentClass);
    }
};

export default reactToWebComponent;
